/** Volt areas (Werkstatt, Accounting) an account sees next to the CRM, from the access token's
 * app_metadata.roles = { crm, fraktion, superadmin, disabled, bv? } (custom_access_token_hook).
 * The MCP enforces the same rule server-side; this only hides links. */

// One entry per Bezirk that runs a Werkstatt. A new Bezirk is a new line here.
export const BEZIRKE: Bezirke = {
  mitte: {
    werkstatt: "https://werkstatt.mitte.volt-fraktionen-hamburg.org",
    books: {
      owner: "Bezirksfraktion Hamburg-Mitte",
      filePrefix: "Volt_HH-Mitte",
    },
  },
};

// The accounting API serves one bank account; this names the Bezirk that owns it.
export const ACCOUNTING_BEZIRK = "mitte";

/** Account holder and CSV file prefix of a Bezirk's books, from BEZIRKE. */
export const accountingBooks = (
  bezirk: string = ACCOUNTING_BEZIRK,
  bezirke: Bezirke = BEZIRKE,
): AccountingBooks => {
  const books = bezirke[bezirk]?.books;
  if (!books) throw new Error(`No accounting books for Bezirk ${bezirk}`);
  return books;
};

export type VoltRoles = {
  superadmin: boolean;
  admin: boolean;
  fraktion: string | null;
  bv: boolean;
  disabled: boolean;
};

export type AccountingBooks = { owner: string; filePrefix: string };

export type Bezirke = Record<
  string,
  { werkstatt: string; books?: AccountingBooks }
>;

export type VoltAreas = {
  werkstatt: { bezirk: string; url: string }[];
  accounting: boolean;
  changelog: boolean;
  attendance: boolean;
};

export const NO_AREAS: VoltAreas = {
  werkstatt: [],
  accounting: false,
  changelog: false,
  attendance: false,
};

const decodeJwtPayload = (token: string): unknown => {
  const part = token.split(".")[1];
  if (!part) return null;
  const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/** Reads the Volt roles from a Supabase access token; null if missing or broken. */
export const readVoltRoles = (
  token: string | null | undefined,
): VoltRoles | null => {
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token) as {
      app_metadata?: { roles?: Record<string, unknown> };
    } | null;
    const roles = payload?.app_metadata?.roles;
    if (!roles || typeof roles !== "object") return null;
    return {
      superadmin: roles.superadmin === true,
      admin: roles.crm === "admin",
      fraktion: typeof roles.fraktion === "string" ? roles.fraktion : null,
      // Same rule as hasBvAccess in the MCP.
      bv:
        roles.bv === "admin" ||
        (Array.isArray(roles.bv) && roles.bv.length > 0),
      disabled: roles.disabled === true,
    };
  } catch {
    return null;
  }
};

/** Werkstatt: superadmin sees every Bezirk, BV members their own. Accounting and changelog: superadmin.
 * Attendance: superadmin and the admins of a Fraktion. */
export const voltAreas = (
  roles: VoltRoles | null,
  bezirke: Bezirke = BEZIRKE,
): VoltAreas => {
  if (!roles || roles.disabled) return NO_AREAS;
  const all = Object.entries(bezirke).map(([bezirk, { werkstatt }]) => ({
    bezirk,
    url: werkstatt,
  }));
  if (roles.superadmin)
    return {
      werkstatt: all,
      accounting: true,
      changelog: true,
      attendance: true,
    };
  return {
    werkstatt: roles.bv ? all.filter((w) => w.bezirk === roles.fraktion) : [],
    accounting: false,
    changelog: false,
    attendance: roles.admin && roles.fraktion !== null,
  };
};
